import { RouteProp } from '@react-navigation/native';
import React from 'react';
import { ProfilesTabParamList, useGoBack } from '../../../../../utils/appNavigationNames';
import useProfileTransferRequest from '../../../../../hooks/api/useProfileTransferRequest';
import { getIntl } from '../../../../../utils/intl';
import getName from '../../../../../utils/getName';
import { ActionButtonLayout } from '../../../../v2BaseComponents/ActionButtonLayout';
import TitledSection from '../../../../v2BaseComponents/TitledSection';
import Text from '../../../../v2BaseComponents/Text';
import MainContentContainer from '../../../../v2BaseComponents/MainContentContainer';
import AnimateLoadedContent from '../../../../v2BaseComponents/Loader/AnimateLoadedContent';
import ProfileHeaderContainer from '../../../../v2BaseComponents/ProfileHeaderContainer';

const intl = getIntl('profile-ownership-transfer-request-details', {
    collaboratorContent:
        'You have requested to transfer profile administration to {toUserFullName}.  You can cancel this request at any time until it is accepted to maintain ownership.',
    nonCollaboratorContent:
        'You have requested to transfer profile administration to a user with the email "{toUserEmail}".  You can cancel this request at any time until it is accepted to maintain ownership.',
    goBack: 'Go Back',
    cancelRequest: 'Cancel Request',
    pendingTransferRequest: 'Pending Transfer Request',
});

export type TransferRequestScreenOwnProps = {};

export type TransferRequestScreenProps = TransferRequestScreenOwnProps & {
    route: RouteProp<ProfilesTabParamList, 'transfer-request'>;
};

const TransferRequestScreen: React.FC<TransferRequestScreenProps> = (props) => {
    const goBack = useGoBack();
    const {
        isLoadingProfileTransferRequest,
        profileTransferRequest,
        isRevokingTransferRequest,
        revokeTransferRequest,
    } = useProfileTransferRequest({
        profileId: props.route.params.profileId,
        onRevokeTransferRequest: () => goBack(),
    });

    return (
        <ProfileHeaderContainer profileId={props.route.params.profileId} bottomBorder omitChatButton>
            <AnimateLoadedContent
                isLoading={
                    isLoadingProfileTransferRequest ||
                    profileTransferRequest.status === 'no-pending-transfer-request' ||
                    isRevokingTransferRequest
                }
            >
                {() => (
                    <ActionButtonLayout
                        whiteBackground
                        scrollable
                        actions={[
                            {
                                title: intl('cancelRequest'),
                                onPress: () => revokeTransferRequest(props.route.params.profileId),
                                isDisabled: isRevokingTransferRequest,
                                variant: 'alert',
                                icon: 'Trash2',
                            },
                        ]}
                    >
                        <MainContentContainer>
                            <TitledSection title={intl('pendingTransferRequest')}>
                                <Text variant="body-1">
                                    {profileTransferRequest.status === 'pending-collaborator-transfer-request'
                                        ? intl('collaboratorContent', {
                                              toUserFullName: getName(profileTransferRequest.transferToUser),
                                          })
                                        : intl('nonCollaboratorContent', {
                                              toUserEmail:
                                                  profileTransferRequest.status === 'no-pending-transfer-request'
                                                      ? ''
                                                      : profileTransferRequest.transferToEmail,
                                          })}
                                </Text>
                            </TitledSection>
                        </MainContentContainer>
                    </ActionButtonLayout>
                )}
            </AnimateLoadedContent>
        </ProfileHeaderContainer>
    );
};

export default React.memo(TransferRequestScreen);
